import { useEffect, useState } from 'react'
import HeaderBox from 'component/PageModalHeaderBox'
import { HeaderWrap, PageScrollWrap, PageWrapperNonScroll } from 'commonStyledComponents'
import {
  Section,
  SectionTitle,
  InfoRow,
  Label,
  Badge,
  CandidateCard,
  DataValBlock,
  MonospaceText,
  EmptyState,
  SafeBottomPadding,
} from './Styled'

type ApiProbeStep = {
  domain: string
  code: number
  headerTime: number
  actualTime: number
  type: 'result' | 'network' | 'timeout'
  valid: boolean
  verified: boolean
  expectedPrefix: string
  dataValContent: string | null
}

type ApiSelectionReport = {
  timestamp: number
  trigger: string
  candidates: ApiProbeStep[]
  selectedDomain: string | null
  selectionReason: string
}

const REASON_LABELS: Record<string, string> = {
  'verified-fastest': 'verified + fastest',
  'unverified-fastest-fallback': 'fallback (no verified, fastest valid)',
  'all-invalid': 'all domains failed',
}

const TYPE_LABELS: Record<string, string> = {
  result: 'OK',
  timeout: 'Timeout',
  network: 'Network Error',
}

const SpeedTestReport = () => {
  const [report, setReport] = useState<ApiSelectionReport | null | undefined>(undefined)

  useEffect(() => {
    if (!navigator.serviceWorker?.controller) {
      setReport(null)
      return
    }
    const sw = navigator.serviceWorker
    const handler = (ev: MessageEvent) => {
      if (ev.data && typeof ev.data === 'object' && ev.data.type === 'speed-report') {
        setReport(ev.data.data ?? null)
      }
    }
    sw.addEventListener('message', handler)
    sw.controller!.postMessage('get-speed-report')
    return () => {
      sw.removeEventListener('message', handler)
    }
  }, [])

  if (report === undefined) {
    return (
      <PageWrapperNonScroll>
        <HeaderWrap>
          <HeaderBox headerTitle="Speed Test Report" />
        </HeaderWrap>
        <EmptyState>Loading...</EmptyState>
      </PageWrapperNonScroll>
    )
  }

  if (!report) {
    return (
      <PageWrapperNonScroll>
        <HeaderWrap>
          <HeaderBox headerTitle="Speed Test Report" />
        </HeaderWrap>
        <EmptyState>No speed test data available (SW may have restarted)</EmptyState>
      </PageWrapperNonScroll>
    )
  }

  const selectedCandidate = report.candidates.find((c) => c.domain === report.selectedDomain)
  const dataValSource = selectedCandidate?.dataValContent
    ? selectedCandidate
    : report.candidates.find((c) => c.dataValContent)

  return (
    <PageWrapperNonScroll>
      <HeaderWrap>
        <HeaderBox headerTitle="Speed Test Report" />
      </HeaderWrap>
      <PageScrollWrap>
        <Section>
          <SectionTitle>Summary</SectionTitle>
          <InfoRow>
            <Label>Time:</Label>
            {new Date(report.timestamp).toLocaleString()}
          </InfoRow>
          <InfoRow>
            <Label>Trigger:</Label>
            {report.trigger}
          </InfoRow>
          <InfoRow>
            <Label>Selected:</Label>
            {report.selectedDomain ?? 'None (all failed)'}
          </InfoRow>
          <InfoRow>
            <Label>Reason:</Label>
            <Badge $success={report.selectionReason !== 'all-invalid'}>
              {REASON_LABELS[report.selectionReason] ?? report.selectionReason}
            </Badge>
          </InfoRow>
        </Section>

        <Section>
          <SectionTitle>Candidates ({report.candidates.length})</SectionTitle>
          {report.candidates.map((c, i) => (
            <CandidateCard key={i}>
              <InfoRow>
                <Label>#{i + 1}</Label>
                <strong>{c.domain}</strong>
                {c.domain === report.selectedDomain && (
                  <>
                    {' '}
                    <Badge $success>SELECTED</Badge>
                  </>
                )}
              </InfoRow>
              <InfoRow>
                <Label>Status:</Label>
                {c.type === 'result' ? `${c.code}` : TYPE_LABELS[c.type]}
                {c.type === 'result' && (
                  <>
                    {' '}
                    <Badge $success={c.valid}>{c.valid ? 'valid' : 'invalid'}</Badge>
                  </>
                )}
              </InfoRow>
              <InfoRow>
                <Label>Header Time:</Label>
                {c.headerTime === Infinity ? '-' : `${c.headerTime}ms`}
                <Label $ml>Total:</Label>
                {c.actualTime === Infinity ? '-' : `${c.actualTime}ms`}
              </InfoRow>
              <InfoRow>
                <Label>Verified:</Label>
                <Badge $success={c.verified}>{c.verified ? 'pass' : 'fail'}</Badge>
              </InfoRow>
              {c.expectedPrefix && (
                <InfoRow>
                  <Label>Expected (sha256 prefix):</Label>
                  <MonospaceText>{c.expectedPrefix}</MonospaceText>
                </InfoRow>
              )}
            </CandidateCard>
          ))}
        </Section>

        {dataValSource?.dataValContent && (
          <Section>
            <SectionTitle>
              data_val Content ({dataValSource.domain})
            </SectionTitle>
            <DataValBlock>{dataValSource.dataValContent}</DataValBlock>
          </Section>
        )}

        <SafeBottomPadding />
      </PageScrollWrap>
    </PageWrapperNonScroll>
  )
}

export default SpeedTestReport
